import React, { useRef, useEffect, useState } from 'react';
import { Collapse, Tooltip, Empty, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';
import { CalculationFunction } from '../calculationMetadataTypes';

const { Panel } = Collapse;

const useStyles = createUseStyles({
    dropdown: {
        position: 'absolute',
        top: '48px',
        left: 0,
        zIndex: 999,
        background: 'white',
        border: '1px solid #d9d9d9',
        borderRadius: '4px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        width: '400px',
        maxHeight: '400px',
        overflow: 'auto',
    },
    header: {
        padding: '12px',
        borderBottom: '1px solid #f0f0f0',
        fontWeight: 600,
        backgroundColor: '#fafafa',
    },
    searchBox: {
        padding: '8px 12px',
        borderBottom: '1px solid #f0f0f0',
    },
    functionItem: {
        padding: '8px 12px',
        cursor: 'pointer',
        borderBottom: '1px solid #f0f0f0',
        '&:hover': {
            backgroundColor: '#e6f7ff',
        },
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    functionName: {
        fontWeight: 500,
        fontSize: '13px',
        fontFamily: 'monospace',
        color: '#1890ff',
    },
    functionDesc: {
        fontSize: '12px',
        color: '#595959',
        marginTop: '4px',
    },
    tooltip: {
        maxWidth: '380px',
        '& .ant-tooltip-inner': {
            backgroundColor: 'white',
            color: '#333',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            borderRadius: '8px',
            padding: '10px',
            border: '1px solid #e0e0e0',
        },
        '& .ant-tooltip-arrow-content': {
            backgroundColor: 'white',
            border: '1px solid #e0e0e0',
        },
    },
    tooltipContent: {
        fontSize: '12px',
        lineHeight: '1.5',
    },
    tooltipTitle: {
        fontSize: '14px',
        fontWeight: 700,
        color: '#1a1a1a',
        marginBottom: '6px',
        fontFamily: 'inherit',
    },
    tooltipSection: {
        marginBottom: '10px',
        '&:last-child': {
            marginBottom: 0,
        },
    },
    tooltipLabel: {
        fontSize: '12px',
        fontWeight: 600,
        color: '#2d3748',
        marginBottom: '6px',
        display: 'block',
    },
    tooltipDescription: {
        fontSize: '12px',
        color: '#666',
        lineHeight: '1.5',
        marginBottom: '0',
    },
    syntaxBox: {
        backgroundColor: '#f5f7fa',
        padding: '8px',
        borderRadius: '6px',
        fontFamily: 'monospace',
        fontSize: '12px',
        color: '#2d3748',
        border: '1px solid #e2e8f0',
        wordBreak: 'break-all',
        whiteSpace: 'pre-wrap',
        lineHeight: '1.5',
    },
    example: {
        backgroundColor: '#f5f7fa',
        padding: '8px',
        borderRadius: '6px',
        fontFamily: 'monospace',
        fontSize: '12px',
        color: '#2d3748',
        border: '1px solid #e2e8f0',
        wordBreak: 'break-all',
        whiteSpace: 'pre-wrap',
        lineHeight: '1.5',
    },
    parameterList: {
        marginTop: '0',
    },
    parameterItem: {
        backgroundColor: '#f8fafc',
        padding: '6px 8px',
        borderRadius: '6px',
        fontSize: '12px',
        color: '#4a5568',
        marginBottom: '4px',
        lineHeight: '1.5',
        '&:last-child': {
            marginBottom: 0,
        },
    },
    parameterName: {
        fontFamily: 'monospace',
        color: '#5e72e4',
        fontWeight: 600,
    },
    emptyContainer: {
        padding: '20px',
    },
    collapse: {
        '& .ant-collapse-header': {
            fontWeight: 500,
        },
    },
});

interface CalculationFunctionPickerProps {
    functions: CalculationFunction[];
    onSelect: (template: string) => void;
    onClose: () => void;
}

export const CalculationFunctionPicker: React.FC<CalculationFunctionPickerProps> = ({
    functions,
    onSelect,
    onClose,
}) => {
    const classes = useStyles();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const { t } = useTranslation();

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const groupedFunctions = functions.reduce((acc, func) => {
        if (!acc[func.category]) {
            acc[func.category] = [];
        }
        acc[func.category].push(func);
        return acc;
    }, {} as Record<string, CalculationFunction[]>);

    const filteredFunctions = searchQuery
        ? functions.filter(
              (f) =>
                  f.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  f.prettyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  f.description.toLowerCase().includes(searchQuery.toLowerCase()),
          )
        : null;

    const renderTooltip = (func: CalculationFunction) => (
        <div className={classes.tooltipContent}>
            <div className={classes.tooltipTitle}>{func.prettyName}</div>

            <div className={classes.tooltipSection}>
                <div className={classes.tooltipDescription}>{func.description}</div>
            </div>

            <div className={classes.tooltipSection}>
                <span className={classes.tooltipLabel}>{t('Syntax')}</span>
                <div className={classes.syntaxBox}>{func.template}</div>
            </div>

            {func.example && (
                <div className={classes.tooltipSection}>
                    <span className={classes.tooltipLabel}>{t('Example')}</span>
                    <div className={classes.example}>{func.example}</div>
                </div>
            )}

            {func.parameters && func.parameters.length > 0 && (
                <div className={classes.tooltipSection}>
                    <span className={classes.tooltipLabel}>{t('Parameters')}</span>
                    <div className={classes.parameterList}>
                        {func.parameters.map((p) => (
                            <div key={p.name} className={classes.parameterItem}>
                                <span className={classes.parameterName}>{p.name}</span>
                                {' - '}
                                {p.description}
                                {p.options && ` (${p.options.join(', ')})`}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    const renderFunction = (func: CalculationFunction) => (
        <Tooltip
            key={func.name}
            title={renderTooltip(func)}
            placement="right"
            overlayClassName={classes.tooltip}
        >
            <div className={classes.functionItem} onClick={() => onSelect(func.template)}>
                <div className={classes.functionName}>{func.name}</div>
                <div className={classes.functionDesc}>{func.description}</div>
            </div>
        </Tooltip>
    );

    return (
        <div ref={dropdownRef} className={classes.dropdown}>
            <div className={classes.header}>{t('Calculation Functions')}</div>

            <div className={classes.searchBox}>
                <Input
                    placeholder={t('Search functions...')}
                    prefix={<SearchOutlined />}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    allowClear
                />
            </div>

            {filteredFunctions ? (
                filteredFunctions.length > 0 ? (
                    filteredFunctions.map(renderFunction)
                ) : (
                    <div className={classes.emptyContainer}>
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={t('No functions found')}
                        />
                    </div>
                )
            ) : functions.length > 0 ? (
                <Collapse
                    bordered={false}
                    defaultActiveKey={Object.keys(groupedFunctions)}
                    className={classes.collapse}
                >
                    {Object.entries(groupedFunctions).map(([category, funcs]) => (
                        <Panel header={category} key={category}>
                            {funcs.map(renderFunction)}
                        </Panel>
                    ))}
                </Collapse>
            ) : (
                <div className={classes.emptyContainer}>
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={t('No calculation functions available')}
                    />
                </div>
            )}
        </div>
    );
};

export default CalculationFunctionPicker;

